import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  SCHEDULE_SCHEMA_VERSION,
  Shift,
  ShiftDraft
} from '../types/schedule.model'
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  SHIFT_TEMPLATES,
  dayjs,
  startOfWeek,
  toDateKey
} from '../utils/schedule'
import { DEFAULT_NIGHT_WINDOW, NightWindow } from '../utils/payroll'
import { SCHEDULE_USE_API } from '../config'

/**
 * The schedule has no backend yet (menu_core_be has no shift entity), so the
 * roster lives in localStorage. Everything the UI needs goes through these
 * actions, which is also the seam the API layer will replace: swap this store
 * for a query hook with the same surface and no component has to change.
 */

interface ScheduleState {
  shifts: Shift[]
  /**
   * Employee colour overrides, hex by user id. Unset means the default.
   *
   * Moves continuously while the picker is dragged, so the roster preview can
   * follow the pointer. NOT what gets saved, see `committedColorsByUserId`.
   */
  colorsByUserId: Record<string, string>
  setEmployeeColor: (userId: string, color: string) => void
  /**
   * The same map as of the last time the user FINISHED picking, pointer
   * released, value typed, colour taken with the dropper, plus whatever the
   * server has sent.
   *
   * The saver watches this rather than `colorsByUserId`, because the picker
   * emits a new colour every 150ms while the pointer is down and each of those
   * is a candidate for a whole-week PUT. Only a finished pick is worth a
   * request.
   */
  committedColorsByUserId: Record<string, string>
  commitEmployeeColors: () => void
  /**
   * Serialized form of each week as last saved to (or loaded from) the backend,
   * keyed by that week's Monday. Compared against the current week to know
   * whether there are unsaved edits. Per week rather than global, so editing
   * March doesn't mark April dirty.
   */
  savedSnapshotByWeek: Record<string, string>
  markWeekSaved: (weekStart: string, snapshot: string) => void
  /**
   * Colours as last saved to (or loaded from) the backend. Separate from the
   * per-week snapshots because colours belong to the restaurant, not to a
   * week: folding them into every week's snapshot made one colour change read
   * as an edit to every loaded week at once. Null until the first read.
   */
  savedColorsSnapshot: string | null
  markColorsSaved: (snapshot: string) => void
  addShift: (draft: ShiftDraft) => void
  updateShift: (id: string, draft: Partial<ShiftDraft>) => void
  removeShift: (id: string) => void
  /** Copies every shift of `sourceWeekStart` onto the following week. */
  copyWeek: (sourceWeekStart: string) => void
  clearWeek: (weekStart: string) => void
  /** One-off demo roster, so the grid isn't empty on first open. */
  seedDemoShifts: (userIds: string[]) => void
  /**
   * The venue's night hours, which the payroll view's NIGHT column counts.
   *
   * Local like the rest of this store: there is no endpoint for it, so a
   * venue that never sets it keeps the default.
   */
  nightWindow: NightWindow
  setNightWindow: (window: NightWindow) => void
  /** Replaces one week wholesale, used when hydrating from the backend. */
  replaceWeek: (
    weekStart: string,
    shifts: Shift[],
    colorsByUserId: Record<string, string>
  ) => void
  seeded: boolean
}

const createId = (): string =>
  `shift-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

/**
 * Baseline for a week that exists only locally, one "copy to next week" has
 * just filled. No real serialized week can equal it, so the week reads dirty
 * and the Save button lights up; without a baseline at all it would read clean
 * and the copies could never be pushed.
 */
export const LOCAL_ONLY_BASELINE = ''

const isInWeek = (date: string, weekStart: string): boolean => {
  const day = dayjs(date)
  const start = dayjs(weekStart)
  return !day.isBefore(start, 'day') && day.isBefore(start.add(7, 'day'), 'day')
}

export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      shifts: [],
      seeded: false,
      colorsByUserId: {},
      committedColorsByUserId: {},
      savedSnapshotByWeek: {},
      savedColorsSnapshot: null,
      nightWindow: DEFAULT_NIGHT_WINDOW,

      setNightWindow: window => set({ nightWindow: window }),

      markColorsSaved: snapshot => set({ savedColorsSnapshot: snapshot }),

      markWeekSaved: (weekStart, snapshot) =>
        set(state => ({
          savedSnapshotByWeek: {
            ...state.savedSnapshotByWeek,
            [weekStart]: snapshot
          }
        })),

      commitEmployeeColors: () =>
        set(state => ({
          committedColorsByUserId: { ...state.colorsByUserId }
        })),

      setEmployeeColor: (userId, color) =>
        set(state => ({
          colorsByUserId: { ...state.colorsByUserId, [userId]: color }
        })),

      addShift: draft =>
        set(state => ({
          shifts: [...state.shifts, { ...draft, id: createId() }]
        })),

      updateShift: (id, draft) =>
        set(state => ({
          shifts: state.shifts.map(shift =>
            shift.id === id ? { ...shift, ...draft } : shift
          )
        })),

      removeShift: id =>
        set(state => ({
          shifts: state.shifts.filter(shift => shift.id !== id)
        })),

      copyWeek: sourceWeekStart =>
        set(state => {
          const source = state.shifts.filter(shift =>
            isInWeek(shift.date, sourceWeekStart)
          )

          // `remoteId` is dropped on purpose: a copy is a NEW shift. Carrying
          // the source's id would ask the backend to move that row into
          // another week, which it refuses with a 403 because the id does not
          // belong to the week being written.
          const copies = source.map(({ remoteId: _remoteId, ...shift }) => ({
            ...shift,
            id: createId(),
            date: toDateKey(dayjs(shift.date).add(7, 'day'))
          }))

          const targetWeekStart = toDateKey(
            dayjs(sourceWeekStart).add(7, 'day')
          )

          return {
            // Replace rather than merge, copying onto a week that already has
            // shifts should mirror the source, not double every entry.
            shifts: [
              ...state.shifts.filter(
                shift => !isInWeek(shift.date, targetWeekStart)
              ),
              ...copies
            ],
            // The target week now holds content that exists nowhere else, so
            // it needs a baseline to read as dirty, see LOCAL_ONLY_BASELINE.
            savedSnapshotByWeek: {
              ...state.savedSnapshotByWeek,
              [targetWeekStart]: LOCAL_ONLY_BASELINE
            }
          }
        }),

      clearWeek: weekStart =>
        set(state => ({
          shifts: state.shifts.filter(shift => !isInWeek(shift.date, weekStart))
        })),

      replaceWeek: (weekStart, shifts, colorsByUserId) =>
        set(state => ({
          shifts: [
            ...state.shifts.filter(shift => !isInWeek(shift.date, weekStart)),
            ...shifts
          ],
          colorsByUserId: { ...state.colorsByUserId, ...colorsByUserId },
          // What the server sent needs no confirming, it is already saved.
          committedColorsByUserId: {
            ...state.committedColorsByUserId,
            ...colorsByUserId
          }
        })),

      seedDemoShifts: userIds => {
        // With the API live the seeded roster would be the first thing a save
        // pushes to the backend, fake shifts for every employee.
        if (SCHEDULE_USE_API || get().seeded || !userIds.length) return

        const monday = startOfWeek(dayjs())
        const seeded: Shift[] = []

        userIds.forEach((userId, userIndex) => {
          // Deterministic pattern: everyone works five of seven days, starting
          // on a different weekday, alternating morning and evening.
          for (let offset = 0; offset < 5; offset += 1) {
            const dayIndex = (userIndex + offset) % 7
            const template =
              SHIFT_TEMPLATES[(userIndex + offset) % 2 === 0 ? 0 : 1]

            seeded.push({
              id: createId(),
              userId,
              date: toDateKey(monday.add(dayIndex, 'day')),
              startMinutes: template.startMinutes,
              endMinutes: template.endMinutes
            })
          }
        })

        set({ shifts: seeded, seeded: true })
      }
    }),
    {
      // Scoped per restaurant. One shared bucket would mean a PUT for one
      // venue carrying another venue's shifts for the same week, the payload
      // is filtered by date, not by tenant.
      name: `employee-schedule-v${SCHEDULE_SCHEMA_VERSION}-${
        localStorage.getItem('restaurantId') ?? 'unknown'
      }`,
      /*
        Only the start used to be kept, with the end always eight hours on.
        Every browser that opened the schedule saved one, chosen or the old
        21:00 default, and keeps those same hours: the new default is for
        venues that have none, never a silent change to a night column.
      */
      merge: (persisted, current) => {
        const { nightStartMinutes, ...saved } = (persisted ?? {}) as Partial<
          ScheduleState & { nightStartMinutes: number }
        >
        return {
          ...current,
          ...saved,
          nightWindow:
            saved.nightWindow ??
            (nightStartMinutes === undefined
              ? current.nightWindow
              : {
                  startMinutes: nightStartMinutes,
                  endMinutes:
                    (nightStartMinutes + 8 * MINUTES_IN_HOUR) % MINUTES_IN_DAY
                })
        }
      }
    }
  )
)
